import type { ComponentChildren } from 'preact';
import * as S from '../app/state';
import { t, LANGS, lang, setLang } from '../i18n';
import { Avatar } from './common';
import { game } from '../game/client';

interface NavItem {
  id: S.Screen;
  glyph: string;
  label: string;
  /** greyed out without a server */
  online?: boolean;
}

const NAV: NavItem[] = [
  { id: 'home', glyph: '⚔', label: 'nav.play' },
  { id: 'heroes', glyph: '☗', label: 'nav.heroes' },
  { id: 'vault', glyph: '▣', label: 'nav.vault', online: true },
  { id: 'pets', glyph: '🐾', label: 'nav.pets' },
  { id: 'squad', glyph: '♟', label: 'nav.squad', online: true },
  { id: 'guild', glyph: '⛨', label: 'nav.guild', online: true },
  { id: 'leaderboards', glyph: '♛', label: 'nav.ranks', online: true },
  { id: 'profile', glyph: '◉', label: 'nav.profile' },
  { id: 'settings', glyph: '⚙', label: 'nav.settings' },
];

/** The menu frame: a navigation rail, the account strip and the current screen. */
export function Shell(props: { children: ComponentChildren }) {
  const acc = S.account.value;
  const online = S.conn.value === 'online';
  const active = S.screen.value === 'build' ? 'home' : S.screen.value;
  const go = (id: S.Screen) => {
    if (id === 'squad' && online && !S.squad.value) game.send({ t: 'squad.create' });
    S.screen.value = id;
  };
  return (
    <div class="shell">
      <nav class="rail panel">
        <div class="logo" onClick={() => go('home')}>
          <span class="mark">K</span>
          <span class="word">{t('app.title')}</span>
        </div>
        {NAV.map((n) => {
          const off = !!n.online && !online;
          return (
            <button class={'nav' + (active === n.id ? ' on' : '') + (off ? ' off' : '')} title={off ? t('toast.needServer') : undefined} disabled={off} onClick={() => go(n.id)}>
              <span class="glyph">{n.glyph}</span>
              <span>{t(n.label)}</span>
              {n.id === 'squad' && S.squad.value && <span class="pill">{S.squad.value.members.length}</span>}
              {n.id === 'vault' && !!acc?.vault?.length && <span class="pill">{acc.vault.length}</span>}
            </button>
          );
        })}
        <div class="grow" />
        <div class="small muted rail-foot">
          {t('label.version')} · {S.ping.value} ms
        </div>
      </nav>
      <header class="shell-head panel">
        <div class="row clickable acct" onClick={() => go('profile')}>
          <Avatar i={acc?.avatar ?? 0} />
          <div class="col" style={{ gap: 0 }}>
            <b>{acc?.name ?? '…'}</b>
            <span class="small muted">{acc?.isGuest ? t('label.guest') : t('label.google.linked')}</span>
          </div>
        </div>
        <div class="fame" title={t('label.fame')}>
          <span class="glyph">✦</span> {acc?.fame ?? 0}
        </div>
        <div class="grow" />
        {S.conn.value === 'error' && (
          <button class="btn small" onClick={() => void game.connect()}>
            {t('app.reconnect')}
          </button>
        )}
        <span class={'badge ' + (online ? 'on' : S.conn.value === 'offline' ? 'off' : '')}>{online ? t('label.online') : S.conn.value === 'connecting' ? t('app.connecting') : t('label.offline')}</span>
        <div class="lang">
          {LANGS.map((l) => (
            <button class={lang.value === l.id ? 'on' : ''} onClick={() => void setLang(l.id)}>
              {l.id.toUpperCase()}
            </button>
          ))}
        </div>
      </header>
      <main class="shell-body">{props.children}</main>
    </div>
  );
}
